import React, {
  useMemo,
  useState,
  Children,
  isValidElement,
  ReactElement,
  JSX,
  useEffect,
  useRef,
} from "react";

export type CodeTabsItemProps = {
  value: string;
  label: string;
  children: React.ReactNode;
};

function CodeTabsItem(_props: CodeTabsItemProps) {
  return null;
}

// Simple shared store for syncing active tab per group
type Subscriber = (value: string) => void;
const sharedStore = new Map<
  string,
  { value: string; subs: Set<Subscriber> }
>();

function ensureGroup(group: string) {
  if (!sharedStore.has(group)) sharedStore.set(group, { value: "", subs: new Set() });
  return sharedStore.get(group)!;
}

export type CodeTabsProps = {
  defaultValue?: string;
  id?: string;
  children: React.ReactNode;
  // optional: group id to sync tabs. Use same group string across components to sync them.
  group?: string;
};

type CodeTabsComponent = React.FC<CodeTabsProps> & {
  Tab: (props: CodeTabsItemProps) => JSX.Element | null;
};

export default function CodeTabs({
  defaultValue,
  id,
  children,
  group = "global",
}: CodeTabsProps) {
  // Collect child items
  const items = useMemo(() => {
    const acc: CodeTabsItemProps[] = [];
    Children.forEach(children, (child) => {
      if (!isValidElement(child)) return;
      const el = child as ReactElement<CodeTabsItemProps>;
      if (el.type === CodeTabsItem) {
        acc.push({ ...el.props });
      }
    });
    return acc;
  }, [children]);

  // initialize shared group if needed
  const grp = group || "global";
  const store = ensureGroup(grp);

  // local state mirrors shared value but ensures fallback to existing items
  const [localActive, setLocalActive] = useState<string>(() => {
    // prefer provided defaultValue, then shared store value, then first item
    if (defaultValue) return defaultValue;
    if (store.value) return store.value;
    return items[0]?.value ?? "";
  });

  // keep ref to avoid re-creating subscriber function
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // subscribe to shared store updates
  useEffect(() => {
    const s = ensureGroup(grp);
    const sub: Subscriber = (v) => {
      // only update if value exists among items, otherwise fallback handled below
      if (!mountedRef.current) return;
      setLocalActive((prev) => (prev === v ? prev : v));
    };
    s.subs.add(sub);
    // if store has no value but a defaultValue exists, broadcast it on mount
    if (!s.value && defaultValue) {
      s.value = defaultValue;
    }
    // ensure local reflects store (and fallback to first item if needed)
    setLocalActive((prev) => {
      const candidate = s.value || prev || items[0]?.value || "";
      return candidate;
    });

    return () => {
      s.subs.delete(sub);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grp, items.map((i) => i.value).join("|")]); // re-subscribe when items change

  // safeActive: ensure the active value exists in items, otherwise fallback to first item
  const safeActive = useMemo(() => {
    if (items.some((t) => t.value === localActive)) return localActive;
    return items[0]?.value ?? "";
  }, [localActive, items]);

  // function to set active both locally and in shared store
  function setActive(value: string) {
    const s = ensureGroup(grp);
    s.value = value;
    // notify subscribers
    s.subs.forEach((cb) => {
      try {
        cb(value);
      } catch {
        // ignore subscriber errors
      }
    });
    // also update local state for this instance
    setLocalActive(value);
  }

  return (
    <div id={id} className="my-4 overflow-hidden rounded-2xl border border-secondary-200">
      {/* Tabs header */}
      <div className="flex flex-wrap items-center gap-1 border-b border-secondary-200 bg-card p-1">
        {items.map((t) => {
          const isActive = t.value === safeActive;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setActive(t.value)}
              className={
                "select-none px-3 py-1.5 text-sm rounded-lg transition font-medium " +
                (isActive
                  ? "text-foreground bg-accent"
                  : "text-foreground/50 hover:text-foreground hover:bg-accent")
              }
              aria-selected={isActive}
              role="tab"
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      <div className="prose prose-sm max-w-none p-4 dark:prose-invert">
        {items.map((t) => (
          <div
            key={t.value}
            role="tabpanel"
            hidden={t.value !== safeActive}
            className="outline-none"
          >
            {t.value === safeActive ? t.children : null}
          </div>
        ))}
      </div>
    </div>
  );
}

(CodeTabs as unknown as CodeTabsComponent).Tab = CodeTabsItem;

export function CodeTab({
  label,
  value,
  lang = "" as string,
  children,
}: {
  label: string;
  value: string;
  lang?: string;
  children: React.ReactNode;
}) {
  return (
    <CodeTabsItem label={label} value={value}>
      <pre>
        <code className={lang ? `language-${lang}` : undefined}>{children}</code>
      </pre>
    </CodeTabsItem>
  );
}
